import {
  Paper,
  Space,
  Stack,
  Title,
  Text,
  SegmentedControl,
  Flex,
} from '@mantine/core';
import { AreaChart } from '@mantine/charts';
import classes from '../../../app.module.css';
import { RefObject, useState } from 'react';
import { chartAxisDateFormatter, getAxisWidth } from '../util';
import { PercentageChange } from './percentageChange';
import { useTranslation } from 'react-i18next';
import { parameterHistoryData } from '../stores';

export const ParameterHistory = ({
  ref,
  parameter,
  data,
  unit,
  infoText,
  parameter2,
  unit2,
  maxWidth,
}: {
  ref?: RefObject<HTMLDivElement | null>;
  parameter: string;
  data: parameterHistoryData[];
  unit: string;
  infoText: string;
  parameter2?: string;
  unit2?: string;
  maxWidth: number;
}) => {
  const [displayedParameter, setDisplayedParameter] = useState('1');
  const { t } = useTranslation('reports');

  function calculateAxisWidth(data: parameterHistoryData[]) {
    const maxValue = () => {
      let max = 0;
      if (displayedParameter === '1') {
        for (const entry of data) {
          if (max < entry.series1) {
            max = entry.series1;
          }
        }
      } else {
        for (const entry of data) {
          if (entry.series2) {
            if (max < entry.series2) {
              max = entry.series2;
            }
          }
        }
      }
      return max;
    };
    return getAxisWidth(maxValue());
  }

  return (
    <Stack ref={ref} gap={24} style={{ scrollSnapAlign: 'center' }}>
      <Paper style={{ padding: 24 }}>
        <Title
          order={5}
          style={{
            width: `calc(min(100vw, ${maxWidth}px) - (2 * var(--mantine-spacing-md) + 48px)`,
          }}
        >
          {t(`reports:${displayedParameter === '1' ? parameter : parameter2}`)}
        </Title>
        <Flex>
          <Title style={{ paddingRight: 'var(--mantine-spacing-xs)' }}>
            {displayedParameter === '1'
              ? data[data.length - 1].series1
              : data[data.length - 1].series2}{' '}
            {displayedParameter === '1' ? unit : unit2}
          </Title>
          {data.length > 1 &&
            (displayedParameter === '1' ? (
              <PercentageChange
                currentValue={data[data.length - 1].series1}
                previousValue={data[data.length - 2].series1}
              />
            ) : (
              <PercentageChange
                currentValue={data[data.length - 1].series2 || 0}
                previousValue={data[data.length - 2].series2 || 0}
              />
            ))}
        </Flex>
        <Space h={'var(--mantine-spacing-md)'} />
        <AreaChart
          h={300}
          data={data}
          dataKey="date"
          withTooltip={false}
          withDots={false}
          tickLine="x"
          series={[
            {
              name: `series${displayedParameter}`,
              color: 'var(--mantine-primary-color-0)',
            },
          ]}
          curveType="linear"
          strokeWidth={3}
          className={classes.areaChart}
          xAxisProps={{
            padding: { left: 24, right: 24 },
            tickFormatter: (value: Date) => chartAxisDateFormatter(value),
            tickLine: { stroke: 'var(--mantine-color-otherGrey10-0)' },
          }}
          yAxisProps={{
            width: calculateAxisWidth(data),
            allowDecimals: false,
            domain: ([dataMin, dataMax]) => {
              const factor = 0.01;
              const min = dataMin - dataMin * factor;
              const max = dataMax + dataMax * factor;
              return [Math.floor(min), Math.ceil(max)];
            },
          }}
        ></AreaChart>
      </Paper>
      {parameter2 && (
        <SegmentedControl
          classNames={{ label: classes.segmentedControlLabelSmall }}
          data={[
            { value: '1', label: t('reports:relative') },
            { value: '2', label: t('reports:absolute') },
          ]}
          onChange={setDisplayedParameter}
        />
      )}
      <Paper>
        <Title order={5}>
          {t(`reports:${parameter}`)}{' '}
          {parameter2 && ` / ${t(`reports:${parameter2}`)}`}
        </Title>
        <Space h={'var(--mantine-spacing-sm)'} />
        <Text>{infoText}</Text>
      </Paper>
    </Stack>
  );
};
